import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";
import { BlogHero } from "@/components/BlogHero";
import { blogHeroUrl, blogHeroAlt } from "@/data/_media/blog-hero";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  articleSchema,
  faqPageSchema,
} from "@/lib/schema";
import { canonicalAndHreflang } from "@/lib/hreflang";

/**
* Static guide: Thailand + Hague Apostille Convention. Covers the transition
* from MFA consular legalization to the Apostille regime and the practical
* steps applicants take today. Targets keyword cluster:
* "hague apostille convention thailand", "thailand apostille member",
* "legalize documents thailand apostille".
*/
const TITLE = "Thailand & the Hague Apostille Convention — Complete Guide";
const DESC =
  "Apostille in Thailand starts 28 February 2027: what changes and what to do with documents today.";
const URL = "/blog/hague-apostille-thailand-guide";
const PUBLISHED = "2026-07-31";

const FAQ = [
  {
    question: "Is Thailand part of the Hague Apostille Convention?",
    answer:
      "Thailand deposited its instrument of accession to the 1961 Apostille Convention on 30 June 2026. Following the standard objection period for existing Contracting States, the Convention enters into force for Thailand on 28 February 2027. Until that date Thailand does not issue Apostilles, and Thai documents still follow the existing legalisation chain.",
  },
  {
    question: "Which Thai authority will issue the Apostille?",
    answer:
      "The Department of Consular Affairs of the Ministry of Foreign Affairs — the Legalisation Division on Chaeng Watthana Road, Bangkok — is the authority that legalises documents today and is the designated competent authority for Apostilles once the Convention takes effect for Thailand.",
  },
  {
    question: "What do I have to do with my documents right now?",
    answer:
      "Exactly what has always applied: the issuing authority or a Notarial Services Attorney certifies the document, the Department of Consular Affairs legalises it, and the destination embassy legalises it where that mission still requires it. Nothing about today's procedure changes before 28 February 2027.",
  },
  {
    question: "How will the Apostille differ from consular legalisation?",
    answer:
      "Under legalisation a Thai document is certified twice — by the Thai MFA and then by the destination country's embassy in Bangkok. Under the Convention a single Apostille issued by the Thai MFA is intended to be accepted by every other Contracting State, removing the embassy step for those destinations.",
  },
  {
    question: "Will I still need a translation?",
    answer:
      "In most cases yes. An Apostille authenticates the signature, seal and capacity of the official who issued the document — it does not translate it. Receiving authorities abroad normally still require a translation that meets their own rules, and Thai documents submitted to the MFA continue to require a translation the Department of Consular Affairs accepts.",
  },
  {
    question: "Can another country object to Thailand's accession?",
    answer:
      "Yes. Under Article 12 of the Convention, existing Contracting States may object to a new accession during the six-month period following deposit; if a state objects, the Convention does not enter into force between that state and Thailand, and legalisation continues for that destination. Check the HCCH status table for Thailand before relying on an Apostille for a specific country.",
  },
];


export const Route = createFileRoute("/blog/hague-apostille-thailand-guide")({
  component: HagueApostilleGuide,
  loader: () => ({ h: (() => ({
    meta: [
      { title: ctrTitle(`${TITLE} — ${SITE.name}`) },
      { name: "description", content: ctrDesc(DESC) },
      { property: "og:title", content: ctrTitle(TITLE) },
      { property: "og:description", content: ctrDesc(DESC) },
      { property: "og:type", content: "article" },
      { property: "og:url", content: `https://${SITE.canonicalHost}${URL}` },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: blogHeroUrl("hague-apostille-thailand-guide") ?? SOCIAL_OG_IMAGE },
      { property: "og:image:alt", content: blogHeroAlt("hague-apostille-thailand-guide", "th") ?? SOCIAL_OG_IMAGE_ALT },
      { name: "twitter:image", content: blogHeroUrl("hague-apostille-thailand-guide") ?? SOCIAL_OG_IMAGE },
      {
        name: "keywords",
        content:
          "hague apostille convention thailand, thailand apostille member, legalize documents thailand apostille, thai apostille MFA, apostille bangkok",
      },
    ],
    links: canonicalAndHreflang(URL),
    scripts: [
      jsonLdScript(
        buildJsonLd([
          webPageSchema({ url: URL, title: TITLE, description: DESC, lastModified: PUBLISHED }),
          breadcrumbListSchema([
            { name: "Home", url: "/" },
            { name: "Blog", url: "/blog" },
            { name: TITLE, url: URL },
          ]),
          articleSchema({
            image: blogHeroUrl("hague-apostille-thailand-guide"),
            url: URL,
            headline: TITLE,
            description: DESC,
            datePublished: PUBLISHED,
            dateModified: PUBLISHED,
          }),
          faqPageSchema(FAQ),
        ]),
      ),
    ],
  }))() }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

function HagueApostilleGuide() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 prose prose-slate dark:prose-invert">
      <nav className="text-sm mb-6 not-prose">
        <Link to="/" className="text-muted-foreground hover:underline">Home</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/blog" className="text-muted-foreground hover:underline">Blog</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span>Hague Apostille — Thailand</span>
      </nav>

      <h1>Thailand &amp; the Hague Apostille Convention — A Complete Guide</h1>
      <BlogHero slug="hague-apostille-thailand-guide" locale="th" />
      <p className="speakable-summary lead answer-summary">
        Thailand deposited its instrument of accession to the 1961 Hague Apostille Convention on
        <strong> 30 June 2026</strong>, and the Convention enters into force for Thailand on
        <strong> 28 February 2027</strong>. Until that date Thailand does <em>not</em> issue
        Apostilles: Thai documents for use abroad still follow the existing chain of
        notarisation / issuing authority → Department of Consular Affairs legalisation →
        destination embassy where required. This guide explains what is changing, what applies
        today, and how to prepare.
      </p>

      <h2>What is an Apostille?</h2>
      <p>
        An Apostille is a standardized certificate — defined by the 1961 Hague Convention
        Abolishing the Requirement of Legalisation for Foreign Public Documents — that a
        competent authority in the country of origin attaches to a public document. Other
        Contracting States then accept that document without further embassy legalisation. It
        authenticates the signature, seal and capacity of the issuing official; it says nothing
        about the content of the document and does not replace a translation.
      </p>

      <h2>Thailand&apos;s accession timeline</h2>
      <ul>
        <li><strong>30 June 2026</strong> — Instrument of accession deposited with the depositary in The Hague.</li>
        <li><strong>1 July 2026 – 28 February 2027</strong> — Objection period for existing Contracting States; the existing legalisation chain continues to apply in full.</li>
        <li><strong>28 February 2027</strong> — The Convention enters into force for Thailand.</li>
      </ul>
      <p className="text-sm">
        Sources:{" "}
        <a href="https://www.hcch.net/en/instruments/conventions/status-table/?cid=41" target="_blank" rel="noreferrer">
          HCCH status table for the Apostille Convention
        </a>{" "}
        and the{" "}
        <a href="https://consular.mfa.go.th/th/publicservice/legalization" target="_blank" rel="noreferrer">
          Department of Consular Affairs, Ministry of Foreign Affairs of Thailand
        </a>.
      </p>

      <h2>Today&apos;s process vs. the process from 28 February 2027</h2>
      <p>
        <strong>Today:</strong> a Thai document destined for, say, the United States needs (1) a
        certified translation, (2) legalisation by the MFA Department of Consular Affairs, and
        (3) legalisation by the destination embassy in Bangkok where that mission requires it.
      </p>
      <p>
        <strong>From 28 February 2027:</strong> for destinations that are Contracting States and
        that have not objected to Thailand&apos;s accession, the same document is expected to need
        only the translation the receiving authority requires plus a single Apostille issued in
        Thailand — the embassy step falls away.
      </p>

      <h2>Who issues Thai legalisation and, later, the Apostille?</h2>
      <p>
        The <strong>Legalisation Division, Department of Consular Affairs, Ministry of Foreign
        Affairs of Thailand</strong> (Chaeng Watthana Road, Bangkok, with regional counters)
        legalises Thai documents today and is the authority designated to issue Apostilles once
        the Convention takes effect for Thailand. Procedural details — forms, counters, fees and
        whether any electronic register is offered — are published by the Department; confirm
        them there before submitting.
      </p>

      <h2>Which documents are covered?</h2>
      <ul>
        <li>Civil registration: birth, marriage, divorce and death certificates, household registration extracts.</li>
        <li>Notarial acts: powers of attorney, affidavits and statutory declarations signed before a Notarial Services Attorney.</li>
        <li>Court documents: judgments, orders and court-certified copies.</li>
        <li>Corporate documents: company affidavits, certificates of incorporation and shareholder registers issued by the Department of Business Development.</li>
        <li>Educational documents: transcripts and diplomas from accredited Thai institutions.</li>
        <li>Police clearance certificates issued by the Royal Thai Police.</li>
      </ul>

      <h2>Objections — where the Apostille may not apply</h2>
      <p>
        Article 12 of the Convention lets an existing Contracting State object to a new
        accession within six months of deposit. Where a state objects, the Convention does not
        enter into force between that state and Thailand and the legalisation chain continues
        for that destination. Because the objection window for Thailand is still open, the
        definitive list is the HCCH status table — we check it per file before advising a route.
      </p>

      <h2>Step-by-step: preparing a Thai document today</h2>
      <ol>
        <li>
          <strong>Prepare the original.</strong> Civil documents are usually re-issued as recent
          originals (many destinations require issuance within 3–6 months). Notarial acts must be
          signed before a licensed Thai Notarial Services Attorney — see our{" "}
          <Link to="/services">Notary Public service</Link>.
        </li>
        <li>
          <strong>Translate.</strong> Translation into the destination language by a translator
          the Department of Consular Affairs accepts — see{" "}
          <Link to="/services">translation services</Link>.
        </li>
        <li>
          <strong>Submit to the Department of Consular Affairs.</strong> Original, translation,
          application form and a copy of the applicant&apos;s ID; standard and express windows are
          published by the Department.
        </li>
        <li>
          <strong>Destination embassy.</strong> Where the mission still requires its own
          legalisation, we book the appointment, submit and collect. From 28 February 2027 this
          step is expected to be replaced by an Apostille for Convention destinations.
        </li>
      </ol>

      <h2>Foreign documents used in Thailand</h2>
      <p>
        The Convention works both ways. Once it is in force for Thailand, a document issued and
        apostilled in another Contracting State is intended to be accepted in Thailand without a
        Thai embassy stamp abroad. Before that date, foreign documents still require legalisation
        by the Thai embassy or consulate in the country of origin. In either case a Thai
        translation is normally required for submission to Thai courts, the Department of
        Provincial Administration and most ministries.
      </p>

      <h2>Common mistakes we see</h2>
      <ul>
        <li>Assuming Thailand already issues Apostilles — it does not before 28 February 2027.</li>
        <li>Submitting a photocopy where the authority accepts only originals or certified copies.</li>
        <li>Using an outdated civil registration extract — many destinations require recent issuance.</li>
        <li>Using a translation that the receiving authority does not recognise.</li>
      </ul>

      <h2>Need help?</h2>
      <p>
        Our licensed Notarial Services Attorneys and translators handle the full workflow — from
        drafting affidavits to MFA submission, embassy legalisation and international courier —
        and will move files to the Apostille route as soon as it is available. Call{" "}
        <a href="tel:+66948958999">094-895-8999</a> or visit our{" "}
        <Link to="/contact">contact page</Link>.
      </p>


      <section className="not-prose mt-12 border-t pt-8">
        <h2 className="text-2xl font-semibold mb-4">Frequently Asked Questions</h2>
        <div className="space-y-4">
          {FAQ.map((f) => (
            <details key={f.question} className="rounded-lg border p-4">
              <summary className="font-medium cursor-pointer">{f.question}</summary>
              <p className="mt-2 text-muted-foreground faq-answer">{f.answer}</p>
            </details>
          ))}
        </div>
      </section>
    </article>
  );
}
